/**
 * Lookup query classification (client-side hints only).
 *
 * Mirrors the server's structural classification in internal/admin/lookup.go:
 * the branch is chosen purely by prefix / "@" — never by trying stores:
 *
 *   "acct_<uuidv7>"  → account
 *   "usr_<uuidv7>"   → internal user
 *   "user_<clerkid>" → Clerk user
 *   contains "@"     → email (keyed hash match server-side; never stored)
 *   anything else    → invalid
 *
 * This module powers the inline hint under the search box. The SERVER remains
 * the authority — classification here never blocks a submit and never decides
 * what is sent.
 */

export type LookupQueryKind = 'email' | 'account' | 'user' | 'clerkUser' | 'invalid';

export interface LookupQueryClassification {
  kind: LookupQueryKind;
  /** Short human hint shown inline; empty for a clean classification. */
  hint: string;
}

/** Server contract: GET /v1/admin/lookup requires 3..=320 chars. */
export const LOOKUP_QUERY_MIN = 3;
export const LOOKUP_QUERY_MAX = 320;

const ACCOUNT_PREFIX = 'acct_';
const USER_PREFIX = 'usr_';
const CLERK_USER_PREFIX = 'user_';

/** Loose email shape: one "@", a dot in the domain, no whitespace. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function classifyQuery(raw: string | null | undefined): LookupQueryClassification {
  const q = (raw ?? '').trim();

  if (q.length === 0) {
    return { kind: 'invalid', hint: 'Enter an email address, account id (acct_…), or user id (usr_… / user_…).' };
  }
  if (q.length < LOOKUP_QUERY_MIN) {
    return { kind: 'invalid', hint: 'Too short — the API requires at least 3 characters.' };
  }
  if (q.length > LOOKUP_QUERY_MAX) {
    return { kind: 'invalid', hint: 'Too long — the API accepts at most 320 characters.' };
  }

  if (q.startsWith(ACCOUNT_PREFIX)) {
    if (q.length <= ACCOUNT_PREFIX.length) {
      return { kind: 'invalid', hint: 'Account ids look like acct_ followed by the id body.' };
    }
    return { kind: 'account', hint: 'Searching by account id.' };
  }

  if (q.startsWith(USER_PREFIX)) {
    if (q.length <= USER_PREFIX.length) {
      return { kind: 'invalid', hint: 'Internal user ids look like usr_ followed by the id body.' };
    }
    return { kind: 'user', hint: 'Searching by internal user id.' };
  }

  if (q.startsWith(CLERK_USER_PREFIX)) {
    if (q.length <= CLERK_USER_PREFIX.length) {
      return { kind: 'invalid', hint: 'Clerk user ids look like user_ followed by the id body.' };
    }
    return { kind: 'clerkUser', hint: 'Searching by Clerk user id.' };
  }

  if (q.includes('@')) {
    if (EMAIL_RE.test(q)) {
      return { kind: 'email', hint: 'Searching by email address (hashed server-side; never stored).' };
    }
    return { kind: 'invalid', hint: 'That does not look like a valid email address.' };
  }

  return {
    kind: 'invalid',
    hint: 'Unrecognized query — use an email address, acct_…, usr_…, or user_… id.',
  };
}
